import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, CheckCircle, TrendingUp, UserPlus, Clock, Users2,
} from 'lucide-react';
import { RETIREMENT_TIERS } from '../../config/dashboardConstants';
import { Target } from 'lucide-react';

const WorkforcePlanningSection = ({ retirementRisk, newHireRetention, staffWorkerRatio }) => (
  <section className="space-y-6" aria-label="Workforce Planning & Risk Dashboard">
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="space-y-2 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <Target className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0">Workforce Planning &amp; Risk</h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-600">
          Strategic workforce sustainability metrics • Retirement risk &amp; new hire retention
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {/* Retirement Risk Funnel — full width on mobile, spans 2 cols × 2 rows on desktop */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 col-span-2 lg:col-span-2 lg:row-span-2 flex flex-col">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900 min-w-0">Retirement Risk Pipeline</h3>
            </div>
            <span className="sm:ml-auto text-xs text-gray-400">Cumulative headcount approaching retirement age</span>
          </div>
          {(() => {
            const tiers = RETIREMENT_TIERS;
            const maxVal = Math.max(
              retirementRisk?.['5yr']?.total || 0,
              retirementRisk?.['3yr']?.total || 0,
              retirementRisk?.['1yr']?.total || 0,
              1
            );
            return (
              <div className="flex-1 flex flex-col justify-center space-y-3">
                {tiers.map(tier => {
                  const total = retirementRisk?.[tier.key]?.total || 0;
                  const staff = retirementRisk?.[tier.key]?.staff || 0;
                  const worker = retirementRisk?.[tier.key]?.worker || 0;
                  const pct = maxVal > 0 ? (total / maxVal) * 100 : 0;
                  return (
                    <div key={tier.key} className={`rounded-lg ${tier.lightColor} px-4 py-3`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-xs font-semibold ${tier.textColor}`}>{tier.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{staff}S / {worker}W</span>
                          <span className={`text-sm font-bold ${tier.textColor}`}>{total}</span>
                        </div>
                      </div>
                      <div className="w-full bg-white/60 rounded-full h-2.5">
                        <div
                          className={`${tier.color} h-2.5 rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* 90-Day Retention */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 h-full flex flex-col justify-center">
          <div className="flex items-start">
            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
              (newHireRetention?.retention_90d?.overall || 0) >= 80 ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 ${
                (newHireRetention?.retention_90d?.overall || 0) >= 80 ? 'text-green-600' : 'text-yellow-600'
              }`} />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                {newHireRetention?.retention_90d?.overall?.toFixed(1) || 0}%
              </p>
              <p className="text-xs sm:text-sm text-gray-600 leading-tight truncate">90-Day Retention</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">Benchmark: 80%+</p>
            </div>
          </div>
        </div>

        {/* 6-Month Retention */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 h-full flex flex-col justify-center">
          <div className="flex items-start">
            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
              (newHireRetention?.retention_6m?.overall || 0) >= 75 ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              <TrendingUp className={`w-4 h-4 sm:w-5 sm:h-5 ${
                (newHireRetention?.retention_6m?.overall || 0) >= 75 ? 'text-green-600' : 'text-yellow-600'
              }`} />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                {newHireRetention?.retention_6m?.overall?.toFixed(1) || 0}%
              </p>
              <p className="text-xs sm:text-sm text-gray-600 leading-tight truncate">6-Month Retention</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">Long-term indicator</p>
            </div>
          </div>
        </div>

        {/* New Hires (12M) */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 h-full flex flex-col justify-center">
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-11 sm:h-11 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                {newHireRetention?.new_hires_last_12m?.total || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 leading-tight truncate">New Hires (12M)</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {newHireRetention?.new_hires_last_12m?.staff || 0}S / {newHireRetention?.new_hires_last_12m?.worker || 0}W
              </p>
            </div>
          </div>
        </div>

        {/* On Probation — same population as Employee Management */}
        <Link
          to="/admin/employees?status=active&substatus=probation"
          className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 h-full flex flex-col justify-center hover:shadow-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          aria-label={`On Probation: ${newHireRetention?.in_probation || 0}. Open employee list filtered to probation.`}
        >
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-11 sm:h-11 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                {newHireRetention?.in_probation || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 leading-tight truncate">On Probation</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {newHireRetention?.in_probation_staff || 0}S / {newHireRetention?.in_probation_worker || 0}W
              </p>
            </div>
          </div>
        </Link>

        {/* Staff-Worker Ratio — full-width slim horizontal card below */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 col-span-2 lg:col-span-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users2 className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-700 leading-tight truncate">Staff : Worker Ratio</p>
              <p className="text-lg sm:text-xl font-bold text-gray-900 leading-tight truncate">
                {staffWorkerRatio?.display ? staffWorkerRatio.display.replace(':', ' : ') : 'N/A'}
              </p>
            </div>
            <span className="text-xs text-gray-500 text-right flex-shrink-0">
              <span className="hidden sm:inline">{staffWorkerRatio?.staff_count || 0} staff : {staffWorkerRatio?.worker_count || 0} workers</span>
              <span className="sm:hidden">{staffWorkerRatio?.staff_count || 0}S / {staffWorkerRatio?.worker_count || 0}W</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default WorkforcePlanningSection;
